import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { api, downloadBookingReceipt, openPdfBlobInNewTab } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { getWhatsAppChatUrl } from '../../constants/contact';
import { formatDisplayName } from '../../utils/formatDisplayName';
import { paystackReturnReference } from '../../utils/paystackReturnReference';
import SEO from '../../components/SEO';
import { PhoneLink } from '../../components/PhoneLink';
import Navbar from '../../component/Navbar/Navbar';
import Footer from '../../component/Footer/Footer';
import {
  FiCalendar,
  FiStar,
  FiCreditCard,
  FiClock,
  FiCheckCircle,
  FiRefreshCw,
  FiDownload,
  FiLogIn,
  FiLogOut,
} from 'react-icons/fi';
import { StarRow } from '../../component/ApartmentReviews/ApartmentReviews';
import '../../component/ApartmentReviews/ApartmentReviews.css';
import { FdPerPageField, normalizeFdPerPage } from '../../components/FdDataTableControls';
import { getPricingBreakdown } from '../../utils/bookingPricing';
import './MyBookings.css';

const STATUS_COLORS = {
  pending: '#f59e0b',
  confirmed: '#3b82f6',
  checked_in: '#10b981',
  checked_out: '#6b7280',
  cancelled: '#ef4444',
};

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  checked_in: 'Checked In',
  checked_out: 'Checked Out',
  cancelled: 'Cancelled',
};

const PAYMENT_LABELS = {
  pending: 'Awaiting Payment',
  partial: 'Partially Paid',
  paid: 'Paid',
  refunded: 'Refunded',
};

const PAYMENT_COLORS = {
  pending: '#f59e0b',
  partial: '#3b82f6',
  paid: '#10b981',
  refunded: '#ef4444',
};

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'pending_payment', label: 'Pending Payment' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const dt = new Date(dateString);
  if (Number.isNaN(dt.getTime())) return String(dateString);
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

/** Local calendar Y-m-d (avoids UTC drift from toISOString for “today” comparisons). */
const localCalendarIsoDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Matches API BookingResource; falls back for older backends. */
const bookingCanDownloadReceipt = (b) => {
  if (typeof b.can_download_receipt === 'boolean') return b.can_download_receipt;
  const paymentStatus = b.payment_status || 'pending';
  const status = b.status || (b.cancelled_at ? 'cancelled' : 'confirmed');
  return paymentStatus === 'paid' && status !== 'cancelled' && !b.cancelled_at;
};

const guestCanCancelBooking = (b) => {
  const status = b.status || (b.cancelled_at ? 'cancelled' : 'confirmed');
  const paymentStatus = b.payment_status || 'pending';
  if (b.cancelled_at) return false;
  if (!['pending', 'confirmed'].includes(status)) return false;
  if (paymentStatus === 'paid') return false;
  return true;
};

/** Prefer `reviews` table payload from API; fall back to legacy booking columns. */
const bookingReviewForDisplay = (b) => {
  if (b.apartment_review) return b.apartment_review;
  if (b.rating || b.review_comment) {
    return { rating: b.rating, comment: b.review_comment || '', created_at: null };
  }
  return null;
};

const bookingCanSubmitReview = (b) => {
  if (typeof b.can_submit_apartment_review === 'boolean') return b.can_submit_apartment_review;
  const status = b.status || (b.cancelled_at ? 'cancelled' : 'confirmed');
  return status === 'checked_out' && !b.rating;
};

const MyBookings = () => {
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const perPage = normalizeFdPerPage(itemsPerPage, 100);
  const [detailsBooking, setDetailsBooking] = useState(null);
  const [payingBookingId, setPayingBookingId] = useState(null);
  const [ratingBookingId, setRatingBookingId] = useState(null);
  const [receiptBookingId, setReceiptBookingId] = useState(null);
  const [ratingForm, setRatingForm] = useState({ rating: 5, comment: '' });
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [checkInBooking, setCheckInBooking] = useState(null);
  const [checkOutBooking, setCheckOutBooking] = useState(null);
  const [checkInFile, setCheckInFile] = useState(null);
  const [checkInIdNumber, setCheckInIdNumber] = useState('');
  const [checkInSubmitting, setCheckInSubmitting] = useState(false);
  const [checkOutNote, setCheckOutNote] = useState('');
  const [checkOutSubmitting, setCheckOutSubmitting] = useState(false);

  const todayCalendarIso = localCalendarIsoDate();

  const normalizeBookingsPayload = useCallback((payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  }, []);

  const loadBookings = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await api.get('/user/bookings');
      setBookings(normalizeBookingsPayload(res));
    } catch (err) {
      setError(err?.message || 'Could not load your bookings');
      setBookings([]);
    } finally {
      setLoading(false);
      if (isRefresh) setRefreshing(false);
    }
  }, [normalizeBookingsPayload]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    loadBookings();
  }, [user, authLoading, loadBookings]);

  useEffect(() => {
    const verifyRef = paystackReturnReference(searchParams);
    if (!verifyRef || !user) return;
    api.post('/bookings/verify-payment', { reference: verifyRef }, null)
      .then((res) => setInfoMessage(res?.message || 'Payment verified successfully.'))
      .catch((err) => setError(err?.message || 'Payment verification failed.'))
      .finally(() => loadBookings(true));
  }, [searchParams, user, loadBookings]);

  const summary = useMemo(() => {
    const total = bookings.length;
    const upcoming = bookings.filter((b) => (
      ['pending', 'confirmed'].includes(b.status) && b.start_date >= todayCalendarIso && !b.cancelled_at
    )).length;
    const active = bookings.filter((b) => b.status === 'checked_in' && !b.cancelled_at).length;
    const completed = bookings.filter((b) => b.status === 'checked_out').length;
    const pendingPayment = bookings.filter((b) => (
      (b.payment_method === 'paystack') && ['pending', 'partial'].includes(b.payment_status) && b.status !== 'cancelled'
    )).length;
    const spent = bookings
      .filter((b) => b.payment_status === 'paid' || b.status === 'checked_out')
      .reduce((sum, b) => sum + Number(b.total_price || 0), 0);
    const nextBooking = bookings
      .filter((b) => ['pending', 'confirmed'].includes(b.status) && b.start_date >= todayCalendarIso && !b.cancelled_at)
      .sort((a, z) => String(a.start_date).localeCompare(String(z.start_date)))[0];

    return { total, upcoming, active, completed, pendingPayment, spent, nextBooking };
  }, [bookings, todayCalendarIso]);

  const visibleBookings = useMemo(() => {
    switch (activeTab) {
      case 'upcoming':
        return bookings.filter((b) => ['pending', 'confirmed'].includes(b.status) && b.start_date >= todayCalendarIso && !b.cancelled_at);
      case 'pending_payment':
        return bookings.filter((b) => b.payment_method === 'paystack' && ['pending', 'partial'].includes(b.payment_status) && b.status !== 'cancelled');
      case 'completed':
        return bookings.filter((b) => b.status === 'checked_out');
      case 'cancelled':
        return bookings.filter((b) => b.status === 'cancelled' || b.cancelled_at);
      default:
        return bookings;
    }
  }, [activeTab, bookings, todayCalendarIso]);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  useEffect(() => {
    setPage(1);
  }, [perPage]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(visibleBookings.length / perPage)),
    [visibleBookings.length, perPage],
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedBookings = useMemo(() => {
    const start = (page - 1) * perPage;
    return visibleBookings.slice(start, start + perPage);
  }, [visibleBookings, page, perPage]);

  const paginationWindow = useMemo(() => {
    const windowSize = 5;
    const start = Math.max(1, page - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    const normalizedStart = Math.max(1, end - windowSize + 1);
    return Array.from({ length: end - normalizedStart + 1 }, (_, i) => normalizedStart + i);
  }, [page, totalPages]);

  const rangeStart = visibleBookings.length === 0 ? 0 : ((page - 1) * perPage) + 1;
  const rangeEnd = Math.min(page * perPage, visibleBookings.length);

  const handleSubmitReview = async (booking) => {
    const aptId = booking.apartment_id;
    if (!aptId) {
      setError('Missing apartment for this booking.');
      return;
    }
    const comment = ratingForm.comment.trim();
    if (comment.length < 10) {
      setError('Please write at least 10 characters for your review.');
      return;
    }
    setError(null);
    setRatingSubmitting(true);
    try {
      await api.post(`/apartments/${aptId}/reviews`, {
        rating: Number(ratingForm.rating),
        comment,
      });
      await loadBookings(true);
      setRatingBookingId(null);
      setRatingForm({ rating: 5, comment: '' });
    } catch (err) {
      if (err?.status === 403) {
        setError(err?.message || 'Verify your email address to leave a review.');
      } else if (err?.status === 422) {
        setError(err?.message || 'You cannot submit this review right now.');
      } else {
        setError(err?.message || 'Could not submit review.');
      }
    } finally {
      setRatingSubmitting(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    const result = await Swal.fire({
      title: 'Cancel this booking?',
      text: 'This action cannot be undone. You will receive a confirmation email once the booking is cancelled.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'Keep booking',
      reverseButtons: true,
      focusCancel: true,
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/user/bookings/${bookingId}`);
      await loadBookings(true);
      Swal.fire({
        title: 'Booking Cancelled',
        text: 'Your booking has been cancelled successfully.',
        icon: 'success',
        confirmButtonColor: 'var(--color-primary, #2563eb)',
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (err) {
      Swal.fire({
        title: 'Cancellation Failed',
        text: err?.message || 'Could not cancel this booking. Please try again.',
        icon: 'error',
        confirmButtonColor: 'var(--color-primary, #2563eb)',
      });
    }
  };

  const handleDownloadReceipt = async (bookingId) => {
    setError(null);
    setInfoMessage('');
    setReceiptBookingId(bookingId);
    try {
      const blob = await downloadBookingReceipt(bookingId);
      const how = openPdfBlobInNewTab(blob, { filename: `luxistt-receipt-${bookingId}.pdf` });
      if (how === 'download') {
        setInfoMessage('Receipt saved to your device. Open the file to view or print.');
      }
    } catch (err) {
      setError(err?.message || 'Could not open receipt.');
    } finally {
      setReceiptBookingId(null);
    }
  };

  const handlePayNow = async (bookingId) => {
    setError(null);
    setInfoMessage('');
    setPayingBookingId(bookingId);
    try {
      const res = await api.post(`/user/bookings/${bookingId}/pay-now`, {});
      if (res?.payment?.authorization_url) {
        window.location.href = res.payment.authorization_url;
        return;
      }
      if (res?.payment?.error) {
        setError(res.payment.error);
      } else {
        setInfoMessage('Payment session created. Please refresh and try again.');
      }
    } catch (err) {
      setError(err?.message || 'Could not initialize payment.');
    } finally {
      setPayingBookingId(null);
    }
  };

  const openCheckInModal = (b) => {
    setError(null);
    setCheckInBooking(b);
    setCheckInFile(null);
    setCheckInIdNumber('');
  };

  const openCheckOutModal = (b) => {
    setError(null);
    setCheckOutBooking(b);
    setCheckOutNote('');
  };

  const handleCheckInSubmit = async () => {
    if (!checkInBooking) return;
    setCheckInSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      if (checkInFile) fd.append('guest_id_document', checkInFile);
      if (checkInIdNumber.trim()) fd.append('guest_id_number', checkInIdNumber.trim());
      await api.post(`/user/bookings/${checkInBooking.id}/check-in`, fd);
      await loadBookings(true);
      setCheckInBooking(null);
      const wa = getWhatsAppChatUrl('Hello Luxistt, I am a guest and need assistance.');
      await Swal.fire({
        title: 'You’re checked in',
        icon: 'success',
        confirmButtonColor: 'var(--color-primary, #0d9488)',
        html: `
          <p style="margin:0 0 0.75rem;text-align:left">Welcome to Luxistt — your check-in time is saved. We’re glad you’re here.</p>
          <ul style="text-align:left;margin:0;padding-left:1.2rem;line-height:1.5">
            <li>Please do not smoke in non-smoking areas.</li>
            <li>If you need anything, message us on <a href="${wa}" target="_blank" rel="noopener noreferrer">WhatsApp</a> (also via the chat button on <a href="https://www.luxistt.com" target="_blank" rel="noopener noreferrer">www.luxistt.com</a>).</li>
            <li>If your building has an intercom, you can use it for on-site help when available.</li>
          </ul>
        `,
        width: '34em',
      });
    } catch (err) {
      setError(err?.message || 'Could not complete check-in.');
    } finally {
      setCheckInSubmitting(false);
    }
  };

  const handleCheckOutSubmit = async () => {
    if (!checkOutBooking) return;
    setCheckOutSubmitting(true);
    setError(null);
    try {
      await api.post(`/user/bookings/${checkOutBooking.id}/check-out`, {
        checkout_guest_note: checkOutNote.trim() || undefined,
      });
      const aptId = checkOutBooking.apartment_id;
      await loadBookings(true);
      setCheckOutBooking(null);
      await Swal.fire({
        title: 'Thank you for staying with us',
        html: `
          <p style="margin:0 0 0.75rem;text-align:left">We’ve recorded your check-out and sent a short thank-you email. We hope to welcome you again soon.</p>
          <p style="margin:0;text-align:left">You can <a href="/login">sign in</a> anytime and leave a star rating and review for this stay from <strong>My Bookings</strong>${aptId ? ` or on the <a href="/apartments/${aptId}">apartment page</a>` : ''}.</p>
        `,
        icon: 'success',
        confirmButtonColor: 'var(--color-primary, #0d9488)',
        width: '34em',
      });
    } catch (err) {
      setError(err?.message || 'Could not complete check-out.');
    } finally {
      setCheckOutSubmitting(false);
    }
  };

  if (!authLoading && !user) {
    return (
      <div className="mybookings-page">
        <Navbar />
        <div className="mybookings-content">
          <div className="mybookings-empty">
            <FiCalendar size={48} />
            <p>Please log in to view your bookings.</p>
            <Link to="/login" className="btn-primary">Log In</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="mybookings-page">
      <SEO title="My Bookings" description="View and manage your Luxistt apartment bookings." path="/my-bookings" noindex />
      <Navbar />
      <div className="mybookings-content">
        <div className="mybookings-header">
          <div>
            <p className="mybookings-welcome">Welcome, {formatDisplayName(user?.name)}</p>
            <h1 className="mybookings-title">My Dashboard</h1>
            <p className="mybookings-subtitle">Your Luxistt short stays, payments, and reviews — all in one place.</p>
          </div>
          <div className="mybookings-header-actions">
            <Link to="/rooms" className="btn-ghost">Browse apartments</Link>
            <button type="button" className="btn-primary" onClick={() => loadBookings(true)} disabled={refreshing || loading}>
              <FiRefreshCw /> {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {!loading && bookings.length > 0 && (
          <div className="mybookings-summary-grid">
            <article className="mybookings-summary-card glass-panel">
              <span className="mybookings-summary-label">Total Bookings</span>
              <strong>{summary.total}</strong>
            </article>
            <article className="mybookings-summary-card glass-panel">
              <span className="mybookings-summary-label">Upcoming</span>
              <strong>{summary.upcoming}</strong>
            </article>
            <article className="mybookings-summary-card glass-panel">
              <span className="mybookings-summary-label">Pending Payment</span>
              <strong>{summary.pendingPayment}</strong>
            </article>
            <article className="mybookings-summary-card glass-panel">
              <span className="mybookings-summary-label">Completed Stays</span>
              <strong>{summary.completed}</strong>
            </article>
          </div>
        )}

        {!loading && bookings.length > 0 && (
          <div className="mybookings-highlight glass-panel">
            <div className="mybookings-highlight-item">
              <FiCheckCircle />
              <span>
                Total Paid: <strong>₦{Number(summary.spent).toLocaleString()}</strong>
              </span>
            </div>
            <div className="mybookings-highlight-item">
              <FiClock />
              <span>
                {summary.nextBooking
                  ? <>Next stay: <strong>{summary.nextBooking.apartment_name}</strong> ({formatDate(summary.nextBooking.start_date)})</>
                  : 'No upcoming stay scheduled yet.'}
              </span>
            </div>
          </div>
        )}

        {(loading || authLoading) && (
          <div className="mybookings-skeleton-list" aria-busy="true" aria-live="polite">
            {[1, 2, 3].map((idx) => (
              <div key={idx} className="mybookings-skeleton-card">
                <div className="mybookings-skeleton-line mybookings-skeleton-line-lg shimmer" />
                <div className="mybookings-skeleton-line mybookings-skeleton-line-md shimmer" />
                <div className="mybookings-skeleton-line mybookings-skeleton-line-sm shimmer" />
              </div>
            ))}
          </div>
        )}
        {error && <div className="mybookings-error">{error}</div>}
        {infoMessage && <div className="mybookings-info">{infoMessage}</div>}

        {!loading && bookings.length === 0 && !error && (
          <div className="mybookings-empty">
            <FiCalendar size={48} />
            <p>You haven't made any bookings yet.</p>
            <Link to="/rooms" className="btn-primary">Browse apartments</Link>
          </div>
        )}

        {!loading && bookings.length > 0 && (
          <div className="mybookings-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`mybookings-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {visibleBookings.length > 0 && (
          <div className="mybookings-list">
            {paginatedBookings.map((b) => {
              const status = b.status || (b.cancelled_at ? 'cancelled' : 'confirmed');
              const canCancel = guestCanCancelBooking(b);
              const canReview = bookingCanSubmitReview(b);
              const reviewShown = bookingReviewForDisplay(b);
              const paymentStatus = b.payment_status || 'pending';
              const paymentMethod = b.payment_method || '';
              const canPayNow = paymentMethod === 'paystack' && ['pending', 'partial'].includes(paymentStatus) && status !== 'cancelled';
              const canReceipt = bookingCanDownloadReceipt(b);
              const canCheckIn = Boolean(b.can_guest_check_in);
              const canCheckOut = Boolean(b.can_guest_check_out);
              return (
                <div key={b.id} className="mybooking-card glass-panel">
                  <div className="mybooking-header">
                    <h3 className="mybooking-apt">{b.apartment_name}</h3>
                    <span className="mybooking-status" style={{ background: STATUS_COLORS[status] || '#6b7280' }}>
                      {STATUS_LABELS[status] || status}
                    </span>
                  </div>
                  <div className="mybooking-dates">
                    <FiCalendar /> {formatDate(b.start_date)} → {formatDate(b.end_date)}
                  </div>
                  <div className="mybooking-details">
                    <span>{b.guests_adults} adult(s){b.guests_children > 0 ? `, ${b.guests_children} child(ren)` : ''}</span>
                    <span className="mybooking-price">₦{Number(b.total_price || 0).toLocaleString()}</span>
                  </div>
                  <div className="mybooking-payment-info">
                    <FiCreditCard />
                    <span className="mybooking-payment-method">
                      {paymentMethod === 'paystack' ? 'Online Payment' : paymentMethod === 'cash' ? 'Cash' : paymentMethod || 'N/A'}
                    </span>
                    <span className="mybooking-payment-badge" style={{ background: PAYMENT_COLORS[paymentStatus] || '#6b7280' }}>
                      {PAYMENT_LABELS[paymentStatus] || paymentStatus}
                    </span>
                  </div>
                  {(b.checked_in_at || b.checked_out_at) && (
                    <div className="mybooking-stay-times">
                      {b.checked_in_at && (
                        <span><FiLogIn aria-hidden /> Checked in <strong>{formatDateTime(b.checked_in_at)}</strong></span>
                      )}
                      {b.checked_out_at && (
                        <span><FiLogOut aria-hidden /> Checked out <strong>{formatDateTime(b.checked_out_at)}</strong></span>
                      )}
                    </div>
                  )}
                  {reviewShown && (
                    <div className="mybooking-review">
                      <FiStar aria-hidden />
                      <span className="mybooking-review-stars">
                        <StarRow value={Number(reviewShown.rating) || 0} size={16} />
                      </span>
                      <span className="mybooking-review-text">
                        {reviewShown.comment || 'No comment'}
                      </span>
                    </div>
                  )}
                  <div className="mybooking-actions">
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => setDetailsBooking(b)}
                    >
                      Booking Details
                    </button>
                    {canPayNow && (
                      <button
                        type="button"
                        className="btn-primary mybooking-paynow"
                        onClick={() => handlePayNow(b.id)}
                        disabled={payingBookingId === b.id}
                      >
                        {payingBookingId === b.id ? 'Starting Payment…' : 'Pay Now'}
                      </button>
                    )}
                    {canReceipt && (
                      <button
                        type="button"
                        className="btn-ghost mybooking-receipt"
                        onClick={() => handleDownloadReceipt(b.id)}
                        disabled={receiptBookingId === b.id}
                      >
                        <FiDownload aria-hidden />
                        {receiptBookingId === b.id ? 'Opening…' : 'View receipt'}
                      </button>
                    )}
                    {canCheckIn && (
                      <button type="button" className="btn-primary mybooking-checkin" onClick={() => openCheckInModal(b)}>
                        <FiLogIn aria-hidden /> Check in
                      </button>
                    )}
                    {canCheckOut && (
                      <button type="button" className="btn-primary mybooking-checkout" onClick={() => openCheckOutModal(b)}>
                        <FiLogOut aria-hidden /> Check out
                      </button>
                    )}
                    {canCancel && (
                      <button type="button" className="btn-ghost mybooking-cancel" onClick={() => handleCancelBooking(b.id)}>
                        Cancel Booking
                      </button>
                    )}
                    {canReview && ratingBookingId !== b.id && (
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => {
                          setRatingBookingId(b.id);
                          setRatingForm({ rating: 5, comment: '' });
                          setError(null);
                        }}
                      >
                        Leave Review
                      </button>
                    )}
                  </div>
                  {ratingBookingId === b.id && (
                    <div className="mybooking-review-form">
                      <p className="mybooking-review-form-hint">Public review on the apartment page (min. 10 characters).</p>
                      <label className="mybooking-review-label">
                        Rating
                        <StarRow
                          value={Number(ratingForm.rating) || 5}
                          interactive
                          size={22}
                          onChange={(n) => setRatingForm((f) => ({ ...f, rating: n }))}
                        />
                      </label>
                      <label className="mybooking-review-label">
                        Your comment
                        <textarea
                          rows={4}
                          minLength={10}
                          maxLength={2000}
                          value={ratingForm.comment}
                          onChange={(e) => setRatingForm((f) => ({ ...f, comment: e.target.value }))}
                          placeholder="What stood out? Comfort, location, cleanliness…"
                        />
                      </label>
                      <div className="mybooking-review-actions">
                        <button type="button" className="btn-ghost" onClick={() => { setRatingBookingId(null); setError(null); }}>Cancel</button>
                        <button type="button" className="btn-primary" onClick={() => handleSubmitReview(b)} disabled={ratingSubmitting}>
                          {ratingSubmitting ? 'Submitting…' : 'Submit Review'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && bookings.length > 0 && visibleBookings.length === 0 && (
          <div className="mybookings-empty mybookings-empty-filtered">
            <FiCalendar size={36} />
            <p>No bookings found in this section.</p>
          </div>
        )}

        {!loading && visibleBookings.length > 0 && (
          <div className="mybookings-pagination-wrap">
            <div className="mybookings-per-page-row">
              <FdPerPageField
                id="my-bookings-per-page"
                label="Bookings per page"
                value={itemsPerPage}
                onChange={setItemsPerPage}
                maxAllowed={100}
              />
            </div>
            <p className="mybookings-pagination-summary">
              Showing {rangeStart}-{rangeEnd} of {visibleBookings.length} bookings
            </p>
            <div className="mybookings-pagination">
              <button
                type="button"
                className="mybookings-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Prev
              </button>
              {paginationWindow.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`mybookings-page-btn${p === page ? ' active' : ''}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                className="mybookings-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {detailsBooking && (
        <div className="mybooking-modal-backdrop" onClick={() => setDetailsBooking(null)}>
          <div
            className="mybooking-modal glass-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Booking details"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mybooking-modal-header">
              <h3>Booking Details</h3>
              <div className="mybooking-modal-header-actions">
                {bookingCanDownloadReceipt(detailsBooking) && (
                  <button
                    type="button"
                    className="btn-primary mybooking-modal-receipt"
                    onClick={() => handleDownloadReceipt(detailsBooking.id)}
                    disabled={receiptBookingId === detailsBooking.id}
                  >
                    <FiDownload aria-hidden />
                    {receiptBookingId === detailsBooking.id ? 'Opening…' : 'View receipt'}
                  </button>
                )}
                <button type="button" className="btn-ghost" onClick={() => setDetailsBooking(null)}>Close</button>
              </div>
            </div>
            <div className="mybooking-modal-grid">
              <div><span className="label">Booking ID</span><strong>#{detailsBooking.id}</strong></div>
              <div><span className="label">Apartment</span><strong>{detailsBooking.apartment_name || '—'}</strong></div>
              <div><span className="label">Check-in</span><strong>{formatDate(detailsBooking.start_date)}</strong></div>
              <div><span className="label">Check-out</span><strong>{formatDate(detailsBooking.end_date)}</strong></div>
              <div><span className="label">Guests</span><strong>{detailsBooking.guests_adults} adult(s){detailsBooking.guests_children ? `, ${detailsBooking.guests_children} child(ren)` : ''}</strong></div>
              {(() => {
                const dpb = getPricingBreakdown(detailsBooking);
                if (dpb.caution_original < 1) {
                  return (
                    <div><span className="label">Total</span><strong>₦{Number(dpb.total).toLocaleString()}</strong></div>
                  );
                }
                return (
                  <>
                    <div><span className="label">Sub total</span><strong>₦{Number(dpb.stay_subtotal).toLocaleString()}</strong></div>
                    <div><span className="label">Caution</span><strong>₦{Number(dpb.caution_display).toLocaleString()}</strong></div>
                    <div><span className="label">Total</span><strong>₦{Number(dpb.total).toLocaleString()}</strong></div>
                  </>
                );
              })()}
              <div><span className="label">Booking Status</span><strong>{STATUS_LABELS[detailsBooking.status] || detailsBooking.status || '—'}</strong></div>
              <div><span className="label">Payment Status</span><strong>{PAYMENT_LABELS[detailsBooking.payment_status] || detailsBooking.payment_status || '—'}</strong></div>
              <div><span className="label">Payment Method</span><strong>{detailsBooking.payment_method || '—'}</strong></div>
              <div><span className="label">Payment Ref</span><strong>{detailsBooking.payment_reference || '—'}</strong></div>
              <div><span className="label">Guest Name</span><strong>{detailsBooking.guest_name || user?.name || '—'}</strong></div>
              <div><span className="label">Guest Email</span><strong>{detailsBooking.guest_email || user?.email || '—'}</strong></div>
              <div>
                <span className="label">Guest Phone</span>
                <strong>
                  <PhoneLink phone={detailsBooking.guest_phone || user?.phone_number} />
                </strong>
              </div>
              <div className="full"><span className="label">Notes</span><strong>{detailsBooking.notes || 'No notes added.'}</strong></div>
              {detailsBooking.checked_in_at && (
                <div><span className="label">Checked in</span><strong>{formatDateTime(detailsBooking.checked_in_at)}</strong></div>
              )}
              {detailsBooking.checked_out_at && (
                <div><span className="label">Checked out</span><strong>{formatDateTime(detailsBooking.checked_out_at)}</strong></div>
              )}
            </div>
          </div>
        </div>
      )}

      {checkInBooking && (
        <div className="mybooking-modal-backdrop" onClick={() => !checkInSubmitting && setCheckInBooking(null)}>
          <div
            className="mybooking-modal glass-panel mybooking-modal--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkin-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mybooking-modal-header">
              <h3 id="checkin-modal-title">Check in</h3>
              <button type="button" className="btn-ghost" onClick={() => !checkInSubmitting && setCheckInBooking(null)} disabled={checkInSubmitting}>Close</button>
            </div>
            <p className="mybooking-modal-lead">
              Confirm your government-issued ID for this stay. You can use an ID already saved on your profile or upload a new photo or PDF.
            </p>
            {checkInBooking.has_government_id_for_checkin ? (
              <p className="mybooking-modal-ok">We already have an ID document on file for you. You can still upload a new file below to replace it for this stay.</p>
            ) : (
              <p className="mybooking-modal-warn">No ID document on file yet — please upload one to complete check-in.</p>
            )}
            <label className="mybooking-review-label" htmlFor="checkin-id-file">
              ID document (JPEG, PNG, WebP, or PDF)
              <input
                id="checkin-id-file"
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={(e) => setCheckInFile(e.target.files?.[0] || null)}
              />
            </label>
            <label className="mybooking-review-label" htmlFor="checkin-id-number">
              ID / passport number (optional)
              <input
                id="checkin-id-number"
                type="text"
                value={checkInIdNumber}
                onChange={(e) => setCheckInIdNumber(e.target.value)}
                placeholder="As shown on your ID"
                autoComplete="off"
              />
            </label>
            <div className="mybooking-modal-actions">
              <button type="button" className="btn-ghost" onClick={() => !checkInSubmitting && setCheckInBooking(null)} disabled={checkInSubmitting}>Cancel</button>
              <button type="button" className="btn-primary" onClick={handleCheckInSubmit} disabled={checkInSubmitting}>
                {checkInSubmitting ? 'Checking in…' : 'Complete check-in'}
              </button>
            </div>
          </div>
        </div>
      )}

      {checkOutBooking && (
        <div className="mybooking-modal-backdrop" onClick={() => !checkOutSubmitting && setCheckOutBooking(null)}>
          <div
            className="mybooking-modal glass-panel mybooking-modal--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mybooking-modal-header">
              <h3 id="checkout-modal-title">Check out</h3>
              <button type="button" className="btn-ghost" onClick={() => !checkOutSubmitting && setCheckOutBooking(null)} disabled={checkOutSubmitting}>Close</button>
            </div>
            <p className="mybooking-modal-lead">
              Add a private note for our team (optional). We’ll send you a thank-you email and you can leave a public review from My Bookings or the apartment page when you’re ready.
            </p>
            <label className="mybooking-review-label" htmlFor="checkout-note">
              Private note (optional)
              <textarea
                id="checkout-note"
                rows={4}
                maxLength={2000}
                value={checkOutNote}
                onChange={(e) => setCheckOutNote(e.target.value)}
                placeholder="Anything we should know about your departure or the apartment…"
              />
            </label>
            <div className="mybooking-modal-actions">
              <button type="button" className="btn-ghost" onClick={() => !checkOutSubmitting && setCheckOutBooking(null)} disabled={checkOutSubmitting}>Cancel</button>
              <button type="button" className="btn-primary" onClick={handleCheckOutSubmit} disabled={checkOutSubmitting}>
                {checkOutSubmitting ? 'Checking out…' : 'Complete check-out'}
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default MyBookings;
